import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Alert,
  Link,
  Paper,
  CircularProgress,
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import identityService from '../../services/identityService';
import '../IdentityLogin/styles.css';

function IdentityConfirmEmail() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get('token');

  const identityUrl = process.env.REACT_APP_IDENTITY_URL || "https://auth.rentbeets.com";

  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const confirmEmail = async () => {
      if (!token) {
        setError('Invalid or missing confirmation token');
        setLoading(false);
        return;
      }

      try {
        const response = await identityService.confirmEmail(token);

        if (response.success) {
          setSuccess(true);
        } else {
          setError(response.message || 'Email confirmation failed');
        }
      } catch (err) {
        if (err.response?.status === 400) {
          setError('Invalid or expired confirmation token');
        } else {
          setError(err.response?.data?.message || 'An error occurred while confirming your email');
        }
      } finally {
        setLoading(false);
      }
    };

    confirmEmail();
  }, [token]);

  if (loading) {
    return (
      <div className="identity-page">
        <Container maxWidth="sm">
          <Box
            sx={{
              marginTop: 8,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
              <Typography
                component="h1"
                variant="h4"
                align="center"
                sx={{
                  fontFamily: 'Bitter, serif',
                  color: '#8B3040',
                  marginBottom: 3,
                }}
              >
                RentBeets
              </Typography>

              <Box sx={{ display: 'flex', justifyContent: 'center', marginBottom: 3 }}>
                <CircularProgress sx={{ color: '#8B3040' }} />
              </Box>

              <Typography variant="body1" align="center">
                Confirming your email address...
              </Typography>
            </Paper>
          </Box>
        </Container>
      </div>
    );
  }

  if (success) {
    return (
      <div className="identity-page">
        <Container maxWidth="sm">
          <Box
            sx={{
              marginTop: 8,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
              <Typography
                component="h1"
                variant="h4"
                align="center"
                sx={{
                  fontFamily: 'Bitter, serif',
                  color: '#8B3040',
                  marginBottom: 3,
                }}
              >
                RentBeets
              </Typography>

              <Alert severity="success" sx={{ marginBottom: 3 }}>
                Email confirmed successfully!
              </Alert>

              <Typography variant="body1" align="center" sx={{ marginBottom: 3 }}>
                Your email has been confirmed. You can now sign in to your account.
              </Typography>

              <Box sx={{ textAlign: 'center' }}>
                <Link
                  href={`${identityUrl}/identity/login?confirmed=true`}
                  variant="body2"
                  sx={{ color: '#8B3040', fontWeight: 'bold' }}
                >
                  Sign In
                </Link>
              </Box>
            </Paper>
          </Box>
        </Container>
      </div>
    );
  }

  return (
    <div className="identity-page">
      <Container maxWidth="sm">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
            <Typography
              component="h1"
              variant="h4"
              align="center"
              sx={{
                fontFamily: 'Bitter, serif',
                color: '#8B3040',
                marginBottom: 3,
              }}
            >
              RentBeets
            </Typography>

            <Alert severity="error" sx={{ marginBottom: 3 }}>
              Email confirmation failed
            </Alert>

            <Typography variant="body1" align="center" sx={{ marginBottom: 3 }}>
              {error}
            </Typography>

            <Box sx={{ textAlign: 'center' }}>
              <Link
                href={`${identityUrl}/identity/login`}
                variant="body2"
                sx={{ color: '#8B3040', fontWeight: 'bold' }}
              >
                Return to Sign In
              </Link>
            </Box>
          </Paper>
        </Box>
      </Container>
    </div>
  );
}

export default IdentityConfirmEmail;
